import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import AdminLayout from '../../components/AdminLayout';
import {
  FaPlus, FaBan, FaCheck, FaTimes, FaSave, FaUser,
  FaEnvelope, FaLock, FaUserShield, FaUserEdit, FaUserTie,
  FaUpload, FaShieldAlt, FaCrown, FaCalendar, FaSearch,
  FaEllipsisV, FaTrash
} from 'react-icons/fa';

const ROLE_CONFIG = {
  admin: { label: 'Admin', icon: FaCrown, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.25)' },
  editor: { label: 'Editor', icon: FaShieldAlt, color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', border: 'rgba(139,92,246,0.25)' },
  uploader: { label: 'Uploader', icon: FaUpload, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.25)' },
};

function getInitials(name) {
  return name ? name.substring(0, 2).toUpperCase() : '??';
}

function getAvatarColor(name) {
  const colors = ['#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#8b5cf6', '#ef4444', '#22d3ee', '#84cc16'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function Users() {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'uploader' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMenu, setActiveMenu] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ username: '', email: '', role: 'uploader' });

  const fetchUsers = () => {
    api.get('/auth/users').then(res => setUsers(res.data)).catch(console.error);
  };

  useEffect(() => { fetchUsers(); }, []);

  // Close menus on outside click
  useEffect(() => {
    if (!activeMenu) return;
    const close = () => setActiveMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [activeMenu]);

  const resetForm = () => {
    setForm({ username: '', email: '', password: '', role: 'uploader' });
    setShowForm(false);
    setError('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/auth/register', form);
      resetForm();
      setSuccess('User created successfully!');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error creating user');
    }
    setSaving(false);
  };

  const handleToggleActive = async (id, currentStatus) => {
    await api.put(`/auth/users/${id}`, { is_active: !currentStatus });
    setSuccess(currentStatus ? 'User disabled' : 'User enabled');
    fetchUsers();
    setActiveMenu(null);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleRoleChange = async (id, newRole) => {
    await api.put(`/auth/users/${id}`, { role: newRole });
    setSuccess('Role updated!');
    fetchUsers();
    setTimeout(() => setSuccess(''), 3000);
  };

  const startEdit = (user) => {
    setEditingUser(user.id);
    setEditForm({ username: user.username, email: user.email, role: user.role });
    setActiveMenu(null);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/auth/users/${editingUser}`, editForm);
      setEditingUser(null);
      setSuccess('User updated!');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error updating user');
      setTimeout(() => setError(''), 3000);
    }
  };

  const filteredUsers = searchQuery
    ? users.filter(u =>
        u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  const stats = {
    total: users.length,
    active: users.filter(u => u.is_active).length,
    admins: users.filter(u => u.role === 'admin').length,
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="um-header">
        <div>
          <h2 className="um-title">User Management</h2>
          <p className="um-subtitle">Manage team access and permissions</p>
        </div>
        <button
          className="rf-btn rf-btn-save"
          onClick={() => { setShowForm(!showForm); setEditingUser(null); }}
        >
          <FaPlus /> Add User
        </button>
      </div>

      {/* Stats */}
      <div className="um-stats">
        <div className="um-stat">
          <div className="um-stat-icon" style={{ background: 'rgba(99,102,241,0.1)', color: '#818cf8' }}><FaUser /></div>
          <div>
            <span className="um-stat-num">{stats.total}</span>
            <span className="um-stat-label">Total Users</span>
          </div>
        </div>
        <div className="um-stat">
          <div className="um-stat-icon" style={{ background: 'rgba(34,197,94,0.1)', color: '#4ade80' }}><FaCheck /></div>
          <div>
            <span className="um-stat-num">{stats.active}</span>
            <span className="um-stat-label">Active</span>
          </div>
        </div>
        <div className="um-stat">
          <div className="um-stat-icon" style={{ background: 'rgba(245,158,11,0.1)', color: '#fbbf24' }}><FaCrown /></div>
          <div>
            <span className="um-stat-num">{stats.admins}</span>
            <span className="um-stat-label">Admins</span>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {success && <div className="rf-alert rf-alert-success"><FaCheck /> {success}</div>}
      {error && !showForm && <div className="rf-alert rf-alert-error"><FaTimes /> {error}</div>}

      {/* Create Form */}
      {showForm && (
        <div className="um-form-panel">
          <div className="um-form-header">
            <h3><FaPlus /> New User</h3>
            <button className="cat-form-close" onClick={resetForm}><FaTimes /></button>
          </div>
          {error && <div className="rf-alert rf-alert-error" style={{ margin: '0 0 16px' }}><FaTimes /> {error}</div>}
          <form onSubmit={handleCreate}>
            <div className="um-form-grid">
              <div className="rf-field">
                <label className="rf-label"><FaUser /> Username <span className="rf-required">*</span></label>
                <input type="text" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} required className="rf-input" placeholder="johndoe" />
              </div>
              <div className="rf-field">
                <label className="rf-label"><FaEnvelope /> Email <span className="rf-required">*</span></label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className="rf-input" placeholder="john@example.com" />
              </div>
              <div className="rf-field">
                <label className="rf-label"><FaLock /> Password <span className="rf-required">*</span></label>
                <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required className="rf-input" placeholder="Min 6 characters" />
              </div>
              <div className="rf-field">
                <label className="rf-label"><FaUserShield /> Role</label>
                <div className="um-role-picker">
                  {Object.entries(ROLE_CONFIG).map(([key, cfg]) => {
                    const Icon = cfg.icon;
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`um-role-option ${form.role === key ? 'um-role-active' : ''}`}
                        style={{
                          '--role-color': cfg.color,
                          '--role-bg': cfg.bg,
                          '--role-border': cfg.border,
                        }}
                        onClick={() => setForm({ ...form, role: key })}
                      >
                        <Icon /> {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="rf-actions" style={{ marginTop: 20, paddingTop: 20 }}>
              <button type="button" onClick={resetForm} className="rf-btn rf-btn-cancel">Cancel</button>
              <button type="submit" className="rf-btn rf-btn-save" disabled={saving}>
                {saving ? 'Creating...' : <><FaSave /> Create User</>}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="cat-search-bar">
        <FaSearch className="cat-search-icon" />
        <input
          type="text"
          placeholder="Search users by name or email..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      {/* User Cards */}
      <div className="um-grid">
        {filteredUsers.map(u => {
          const role = ROLE_CONFIG[u.role] || ROLE_CONFIG.uploader;
          const RoleIcon = role.icon;
          const avatarColor = getAvatarColor(u.username);
          const isEditing = editingUser === u.id;

          return (
            <div key={u.id} className={`um-card ${!u.is_active ? 'um-card-disabled' : ''} ${isEditing ? 'um-card-editing' : ''}`}>
              {/* Status dot */}
              <div className={`um-status-dot ${u.is_active ? 'um-status-active' : 'um-status-inactive'}`} />

              {/* Menu button */}
              <div className="um-card-menu-wrap">
                <button
                  className="um-card-menu-btn"
                  onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === u.id ? null : u.id); }}
                >
                  <FaEllipsisV />
                </button>
                {activeMenu === u.id && (
                  <div className="um-dropdown" onClick={e => e.stopPropagation()}>
                    <button onClick={() => startEdit(u)}>
                      <FaUserEdit /> Edit User
                    </button>
                    <button onClick={() => handleToggleActive(u.id, u.is_active)}>
                      {u.is_active ? <><FaBan /> Disable</> : <><FaCheck /> Enable</>}
                    </button>
                  </div>
                )}
              </div>

              {isEditing ? (
                /* Edit Mode */
                <div className="um-card-edit">
                  <div className="um-card-avatar" style={{ background: avatarColor, marginBottom: 16 }}>
                    <FaUserEdit style={{ fontSize: 18 }} />
                  </div>
                  <div className="rf-field" style={{ marginBottom: 12 }}>
                    <input type="text" value={editForm.username} onChange={e => setEditForm({ ...editForm, username: e.target.value })} className="rf-input" placeholder="Username" />
                  </div>
                  <div className="rf-field" style={{ marginBottom: 12 }}>
                    <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="rf-input" placeholder="Email" />
                  </div>
                  <div className="rf-field" style={{ marginBottom: 16 }}>
                    <div className="um-role-picker um-role-picker-sm">
                      {Object.entries(ROLE_CONFIG).map(([key, cfg]) => {
                        const Icon = cfg.icon;
                        return (
                          <button
                            key={key}
                            type="button"
                            className={`um-role-option ${editForm.role === key ? 'um-role-active' : ''}`}
                            style={{ '--role-color': cfg.color, '--role-bg': cfg.bg, '--role-border': cfg.border }}
                            onClick={() => setEditForm({ ...editForm, role: key })}
                          >
                            <Icon />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="rf-btn rf-btn-cancel" style={{ flex: 1, padding: '8px 0', fontSize: 12 }} onClick={() => setEditingUser(null)}>Cancel</button>
                    <button className="rf-btn rf-btn-save" style={{ flex: 1, padding: '8px 0', fontSize: 12 }} onClick={saveEdit}><FaSave /> Save</button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <>
                  <div className="um-card-avatar" style={{ background: avatarColor }}>
                    {getInitials(u.username)}
                  </div>
                  <h4 className="um-card-name">{u.username}</h4>
                  <span className="um-card-email">{u.email}</span>
                  <div className="um-card-role" style={{ background: role.bg, color: role.color, borderColor: role.border }}>
                    <RoleIcon /> {role.label}
                  </div>
                  <div className="um-card-meta">
                    <FaCalendar /> {new Date(u.created_at).toLocaleDateString()}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {filteredUsers.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
          {searchQuery ? `No users match "${searchQuery}"` : 'No users yet.'}
        </div>
      )}
    </AdminLayout>
  );
}

export default Users;
